import type { DistanceTripDetail } from '../distance-agreement.api';
import { OUTCOME_LABELS, formatKm, formatPercent, stateExplanation } from '../policies/distance-labels.policy';

const when = (iso: string) => new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

/** How the gap was answered, newest first; an answer that was superseded stays on the list. */
export function ResolutionHistory({ resolutions }: Pick<DistanceTripDetail, 'resolutions'>) {
  return (
    <section aria-label="Resolutions" className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-fg">Resolutions</h2>
      {resolutions.length === 0 ? (
        <p className="text-sm text-muted">Nobody has answered this yet.</p>
      ) : (
        <ol className="flex flex-col gap-2">
          {resolutions.map((resolution, index) => (
            <li key={resolution.id} className="rounded-lg border border-line bg-surface p-3">
              <p className="font-medium">
                {OUTCOME_LABELS[resolution.outcome]}
                {index > 0 && <span className="ml-2 text-xs font-normal text-muted">Superseded</span>}
              </p>
              <p className="text-sm">{resolution.note}</p>
              <p className="text-xs text-muted">
                {resolution.resolvedByName ?? 'Someone'} · {when(resolution.createdAt)}
              </p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/** Every time the trip was judged, with the GPS figure it was judged on: a figure a resolver set aside is still here. */
export function JudgementHistory({ history }: Pick<DistanceTripDetail, 'history'>) {
  return (
    <section aria-label="Comparison history" className="flex flex-col gap-2">
      <h2 className="text-base font-semibold text-fg">Comparison history</h2>
      <ol className="flex flex-col gap-2">
        {history.map((entry) => (
          <li key={entry.id} className="rounded-lg border border-line bg-surface p-3 text-sm">
            <p>
              GPS {entry.gpsDistanceKm === null ? 'not available' : formatKm(entry.gpsDistanceKm)} · odometer{' '}
              {entry.odometerDistanceKm === null ? 'not available' : formatKm(entry.odometerDistanceKm)}
              {entry.gapPercent !== null && ` · gap ${formatPercent(entry.gapPercent)}`}
            </p>
            <p className="text-xs text-muted">
              {stateExplanation(entry) ?? 'Within tolerance'} · tolerance {entry.tolerancePercent}% · {when(entry.judgedAt)}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}
